import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Core, Decorative } from "@faire/design-tokens";
import { useInsiderState } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderState";
import { useInsiderTrialingPeriod } from "@faire/retailer-visitor-shared/hooks/insider/useInsiderTrialingPeriod";
import { useIsLoggedInRetailer } from "@faire/retailer-visitor-shared/lib/isLoggedInRetailer";
import { usePopSignUp, SignUpType, SignUpElement } from "@faire/retailer-visitor-shared/lib/popSignUp";
import { useUserStore } from "@faire/retailer-visitor-shared/stores/domain/UserStore";
import { InsiderMarketingPageCTA } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/__internal__/InsiderMarketingPageCTA";
import { StyledButton } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/__internal__/styled";
import { RejoinInsiderMsg } from "@faire/retailer-visitor-shared/views/user/InsiderMarketing/components/consts";
import { optimizeImageUrlFillRectangle } from "@faire/web--source/common/images/optimizeImages";
import { getCurrentPath } from "@faire/web--source/common/Path";
import { useIsSSR } from "@faire/web--source/common/server-side-rendering/isSSR";
import { Insider } from "@faire/web--source/slate/icons/InsiderIcon";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useDoActionWhenVisible } from "@faire/web--source/ui/hooks/useDoActionWhenVisible";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackInsiderIntlMarketingPageFooterCtaClick } from "@faire/web-api--source/events/insider/click/intlMarketingPageFooterCta";
import { trackInsiderLoggedOutMarketingPageFooterCtaClick } from "@faire/web-api--source/events/insider/click/loggedOutMarketingPageFooterCta";
import { trackInsiderLoggedOutMarketingPageFooterView } from "@faire/web-api--source/events/insider/view/loggedOutMarketingPageFooter";
import { IInsiderModalRequest } from "@faire/web-api--source/indigofair/insider/IInsiderModalRequest";
import React from "react";
import styled from "styled-components";
export var InsiderFooter = function() {
    var isSSR = useIsSSR();
    var ref = useDoActionWhenVisible(function() {
        trackInsiderLoggedOutMarketingPageFooterView();
    });
    var image = useResponsiveValue({
        xLargeAndAbove: optimizeImageUrlFillRectangle({
            url: "https://cdn.faire.com/fastly/0a8aa1f77b22a1c9e9cbe54f5ed09b90f84569c3c0e1bee1e0f99edd3628fda3.png"
        }, isSSR, 693, 475),
        mobileAndAbove: optimizeImageUrlFillRectangle({
            url: "https://cdn.faire.com/fastly/0a8aa1f77b22a1c9e9cbe54f5ed09b90f84569c3c0e1bee1e0f99edd3628fda3.png"
        }, isSSR, 571, 475)
    });
    var isLoggedInRetailer = useIsLoggedInRetailer();
    return /*#__PURE__*/ _jsx(Wrapper, {
        ref: ref,
        children: /*#__PURE__*/ _jsxs(Container, {
            children: [
                /*#__PURE__*/ _jsx(Image, {
                    src: image
                }),
                /*#__PURE__*/ _jsxs(ContentWrapper, {
                    children: [
                        /*#__PURE__*/ _jsxs(Row, {
                            align: "center",
                            children: [
                                /*#__PURE__*/ _jsx(Insider, {
                                    fontSize: "24px",
                                    color: Core.icon.inverse,
                                    fill: "transparent"
                                }),
                                /*#__PURE__*/ _jsx(Spacer, {
                                    width: "1x"
                                }),
                                /*#__PURE__*/ _jsx(Typography, {
                                    variant: "subheadingSansMedium",
                                    color: Core.text.inverse,
                                    children: "Insider"
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "1x"
                        }),
                        /*#__PURE__*/ _jsx(Heading, {
                            variant: {
                                mobileAndAbove: "displaySSerifRegular",
                                tabletAndAbove: "displayLSerifRegular"
                            },
                            color: Core.text.inverse,
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "LzsPgx",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Discover how much you could save with Insider."
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "3x"
                        }),
                        /*#__PURE__*/ _jsx(Typography, {
                            variant: "subheadingSansMedium",
                            color: Core.text.inverse,
                            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                                id: "x+hkuI",
                                defaultMessage: [
                                    {
                                        type: 0,
                                        value: "Ready to become a member?"
                                    }
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spacer, {
                            height: "3x"
                        }),
                        /*#__PURE__*/ _jsx(InsiderMarketingPageCTA, {
                            cta: /*#__PURE__*/ _jsx(FooterCTA, {}),
                            hideTooltip: !isLoggedInRetailer
                        })
                    ]
                })
            ]
        })
    });
};
var FooterCTA = function() {
    var userCanPlaceOrder = useUserStore([
        "userCanPlaceOrder"
    ]).userCanPlaceOrder;
    var disableCTA = !userCanPlaceOrder;
    var isLoggedInRetailer = useIsLoggedInRetailer();
    var popSignUp = usePopSignUp().popSignUp;
    var handleSignUp = function() {
        return popSignUp(SignUpType.GENERIC, trackInsiderLoggedOutMarketingPageFooterCtaClick, {
            element: SignUpElement.FOOTER,
            sourceId: "insider_footer_cta"
        });
    };
    if (!isLoggedInRetailer) {
        return /*#__PURE__*/ _jsx(StyledButton, {
            "data-test-id": "insider-footer-enroll-cta",
            variant: "secondary",
            onClick: handleSignUp,
            children: /*#__PURE__*/ _jsx(StrictLocalMsg, {
                id: "/tvCT1",
                defaultMessage: [
                    {
                        type: 0,
                        value: "Sign up to shop"
                    }
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(StyledButton, {
        disabled: disableCTA,
        "data-test-id": "insider-footer-enroll-cta",
        variant: "secondary",
        onClick: trackInsiderIntlMarketingPageFooterCtaClick,
        linkTo: getCurrentPath().append({
            insiderEnrolment: IInsiderModalRequest.InsiderModalContext.MARKETING_PAGE
        }).toString(),
        children: /*#__PURE__*/ _jsx(CtaText, {})
    });
};
var Wrapper = styled(Row).attrs({
    justify: "center",
    align: "center"
}).withConfig({
    componentId: "sc-44b4f2b-0"
})([
    "position:relative;width:100%;background-color:",
    ";overflow:hidden;min-height:352px;",
    "{min-height:338px;}",
    "{min-height:419px;}",
    "{min-height:475px;}"
], Decorative.shade.teal, responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.xLargeAndAbove);
// eslint-disable-next-line @faire/prefer-slate-attrs -- false alarm
var Container = styled(Row).attrs({
    justify: "space-between"
}).withConfig({
    componentId: "sc-44b4f2b-1"
})([
    "width:100%;max-width:1024px;",
    '{&:before{display:block;content:"";width:578px;height:419px;}}'
], responsiveMediaQueries.desktopAndAbove);
var Image = styled.img.withConfig({
    componentId: "sc-44b4f2b-2"
})([
    "position:absolute;display:none;width:auto;left:0;bottom:0;",
    "{display:initial;height:419px;}",
    "{height:475px;}"
], responsiveMediaQueries.desktopAndAbove, responsiveMediaQueries.xLargeAndAbove);
var ContentWrapper = styled(Column).attrs({
    justify: "center",
    align: "flex-start"
}).withConfig({
    componentId: "sc-44b4f2b-3"
})([
    "width:100%;padding:",
    ";",
    "{padding:",
    ";}",
    "{flex:1;max-width:651px;padding:",
    ";}"
], getSpacing("5x"), responsiveMediaQueries.tabletAndAbove, getSpacing("5x", "7x"), responsiveMediaQueries.desktopAndAbove, getSpacing("3x", "5x", "3x", 0));
var Heading = styled(Typography).withConfig({
    componentId: "sc-44b4f2b-4"
})([
    "max-width:585px;"
]);
var CtaText = function() {
    var isInsiderCancelled = useInsiderState().isInsiderCancelled;
    var trialingPeriodDays = useInsiderTrialingPeriod().trialingPeriodDays;
    if (isInsiderCancelled) {
        return /*#__PURE__*/ _jsx(RejoinInsiderMsg, {});
    }
    return /*#__PURE__*/ _jsx(StrictLocalMsg, {
        id: "f0MLC7",
        defaultMessage: [
            {
                type: 0,
                value: "Start "
            },
            {
                type: 1,
                value: "trialingDaysNumber"
            },
            {
                type: 0,
                value: "-day free trial"
            }
        ],
        values: {
            trialingDaysNumber: trialingPeriodDays
        }
    });
};
